import type { SelectChangeEvent } from "@mui/material";
import { MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import LeftAlignment from "assets/img/svg/leftAlignment";
import { borderValue } from "components/ImportData";
import type { TImportAcceptedKeys, TImportImmaterielKeys, TImportTitles } from "components/ImportData/import_data";
import { applyColumnMappingToRows, resolveColumnMapping } from "components/ImportData/resolveColumnMapping";
import type { TObj } from "components/ImportData/type";
import { removeMandatoryField } from "components/ImportData/utils";
import { Spinner } from "components/shared/Spinner";
import type { IChannel } from "containers/Channels/types";
import { getJsonOrFalse, isObjectEmpty } from "helpers/utils";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import StyledErrorSelect from "../TableErrorDropdown";

type TProps = {
    importTitles: TImportTitles;
    dictionaryKey: string;
    fileTitles: string[];
    selectValues: TObj;
    array: any[];
    loadedData: TObj[];
    importAcceptedKeys: TImportAcceptedKeys | TImportImmaterielKeys;
    channelData?: IChannel;
    setNotMatched: React.Dispatch<React.SetStateAction<number | null>>;
    setSelectValue: React.Dispatch<React.SetStateAction<TObj>>;
    setLoadedData: React.Dispatch<React.SetStateAction<TObj[]>>;
    isImmateriel: boolean;
    currentChannelId: number | string;
    usesMultiPeriods: boolean;
    setMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    mandatoryKeys: string[];
};

const headerTableCell = {
    width: "320px",
    padding: "16px 0",
};

const MappingColumns = ({
    importTitles,
    dictionaryKey,
    fileTitles,
    selectValues,
    setNotMatched,
    setSelectValue,
    setLoadedData,
    loadedData,
    importAcceptedKeys,
    array,
    channelData,
    isImmateriel,
    currentChannelId,
    usesMultiPeriods,
    setMapping,
    mandatoryKeys,
}: TProps) => {
    const [t] = useTranslation();

    const [mandatoryFields, setMandatoryFields] = useState(mandatoryKeys);
    const [isInitialMappingReady, setIsInitialMappingReady] = useState(false);

    const handleChangeSelect = useCallback(
        (e: SelectChangeEvent<unknown>) => {
            if (!selectValues[e.target.name]) {
                setNotMatched((prev) => (prev == null ? prev : prev - 1));
            }
            setSelectValue((prev) => ({
                ...prev,
                [e.target.name]: e.target.value as string,
            }));
        },
        [selectValues, setNotMatched, setSelectValue],
    );

    const handleSetEmpty = (titleKey: string) => {
        setMapping((prev) => ({
            ...prev,
            [titleKey]: "NotMandatory",
        }));

        setLoadedData((prevData) =>
            prevData.map((item) => {
                const next = { ...item };
                delete next[titleKey];
                const label = importTitles[titleKey];
                if (label) delete next[label];
                return next;
            }),
        );
    };

    const handleChangeValue = (titleKey: string, titleName: string) => {
        setMapping((prev) => ({
            ...prev,
            [titleKey]: titleName,
        }));

        setLoadedData((prevData) =>
            prevData.map((item, index) => ({
                ...item,
                [titleKey]: array[index][titleName] || "",
                [importTitles[titleKey]]: array[index][titleName] || "",
            })),
        );
    };

    useEffect(() => {
        const isCurrentChannelConsolidatedSalesImport = currentChannelId === -1;
        if (isCurrentChannelConsolidatedSalesImport) {
            setMandatoryFields((prev) => [...prev, "channelId"]);
        } else if (mandatoryFields.includes("channelId")) {
            const newFields = removeMandatoryField("channelId", mandatoryFields);
            newFields && setMandatoryFields((prev) => ({ ...prev, ...newFields }));
        }

        if (usesMultiPeriods) {
            setMandatoryFields((prev) => [...prev, "date"]);
        } else if (mandatoryFields.includes("date")) {
            const newFields = removeMandatoryField("date", mandatoryFields);
            newFields && setMandatoryFields((prev) => ({ ...prev, ...newFields }));
        }
    }, [currentChannelId, usesMultiPeriods]);

    useEffect(() => {
        if (!isInitialMappingReady) return;

        const titlesWithoutImmediateMatch = Object.values(importTitles).filter(
            (title: string) => !Object.keys(selectValues).includes(title),
        ).length;

        setNotMatched(titlesWithoutImmediateMatch);
    }, [fileTitles, selectValues, importTitles, isInitialMappingReady, setNotMatched]);

    useEffect(() => {
        const preferenceMappingColumns = getJsonOrFalse(localStorage.getItem("preferenceMappingColumns") ?? "{}");
        const currentPrefMappingColumns = preferenceMappingColumns[dictionaryKey];
        const savedPreferences =
            currentPrefMappingColumns && !isObjectEmpty(currentPrefMappingColumns)
                ? currentPrefMappingColumns
                : undefined;

        const mandatoryForMapping = [...mandatoryKeys];
        if (currentChannelId === -1 && !mandatoryForMapping.includes("channelId")) {
            mandatoryForMapping.push("channelId");
        }
        if (usesMultiPeriods && !mandatoryForMapping.includes("date")) {
            mandatoryForMapping.push("date");
        }

        const { byApiKey, byLabel } = resolveColumnMapping({
            importTitles,
            importAcceptedKeys,
            fileTitles,
            savedPreferences,
            isImmateriel,
            channelType: channelData?.type,
            mandatoryKeys: mandatoryForMapping,
        });

        if (Object.keys(byLabel).length > 0) {
            setSelectValue((prev) => ({ ...prev, ...byLabel }));
            setMapping((prev) => ({ ...prev, ...byApiKey }));
            const sourceRows = array.length > 0 ? array : loadedData;
            setLoadedData(applyColumnMappingToRows(sourceRows, byApiKey, importTitles));
        }

        setIsInitialMappingReady(true);
    }, []);

    useEffect(() => {
        const preferenceMappingColumns = getJsonOrFalse(localStorage.getItem("preferenceMappingColumns") ?? "{}");
        if (preferenceMappingColumns) {
            preferenceMappingColumns[dictionaryKey] = selectValues;
            localStorage.setItem("preferenceMappingColumns", JSON.stringify(preferenceMappingColumns));
        }
    }, [selectValues]);

    if (!isInitialMappingReady) {
        return (
            <div className="mapping-columns-pending">
                <Spinner size={48} />
            </div>
        );
    }

    return (
        <Paper
            sx={{
                minWidth: "320px",
                border: "none",
                overflow: "hidden",
                marginBottom: 0,
                borderRadius: 0,
                boxShadow: "none",
            }}
        >
            <TableContainer sx={{ maxHeight: "400px" }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerTableCell}>
                                <div className="table-title">
                                    <LeftAlignment />
                                    <span>{t("ImportData.TemplateFileTitles")}</span>
                                </div>
                            </TableCell>
                            <TableCell sx={headerTableCell}>
                                <div className="table-title">
                                    <LeftAlignment />
                                    <span>{t("ImportData.FileTitles")}</span>
                                </div>
                            </TableCell>
                            <TableCell sx={headerTableCell}>
                                <div className="table-title">
                                    <LeftAlignment />
                                    <span>{t("ImportData.ExampleData")}</span>
                                </div>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {Object.keys(importTitles).map((titleKey, index) => {
                            const titleValue = importTitles[titleKey];
                            const columnSuccessfullyMapped = Object.keys(selectValues).find(
                                (item) => item === titleValue,
                            );

                            return (
                                <TableRow key={`titleKey-${index}`}>
                                    <TableCell
                                        sx={{
                                            borderLeft: "1px solid rgba(224, 224, 224, 1)",
                                            backgroundColor: "#f6f6f6",
                                            borderColor: "rgba(224, 224, 224, 1)",
                                            width: "320px",
                                            padding: "0 16px",
                                            borderRight: borderValue,
                                            borderBottom: borderValue,
                                            fontFamily: "Poppins, sans-serif",
                                            fontWeight: "500",
                                        }}
                                    >
                                        {titleValue}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            width: "320px",
                                            padding: 0,
                                            borderRight: borderValue,
                                        }}
                                    >
                                        <StyledErrorSelect
                                            name={titleValue}
                                            value={selectValues[titleValue] ?? ""}
                                            onChange={handleChangeSelect}
                                            displayEmpty
                                            sx={{
                                                padding: "10px",
                                                color: !columnSuccessfullyMapped ? "#A16262" : "#000",
                                                backgroundColor: !columnSuccessfullyMapped
                                                    ? "rgba(197, 83, 83, 0.12)"
                                                    : "#fff",
                                                fontSize: "12px",
                                                lineHeight: "18px",
                                                fontFamily: "Poppins, sans-serif",
                                            }}
                                            fullWidth
                                            renderValue={
                                                !columnSuccessfullyMapped
                                                    ? () => <span>{t("ImportData.NotMatchedColumn")}</span>
                                                    : undefined
                                            }
                                        >
                                            {!mandatoryFields.includes(titleKey) && (
                                                <MenuItem
                                                    key="not-mondatory"
                                                    value="NotMandatory"
                                                    onClick={() => handleSetEmpty(titleKey)}
                                                    sx={{ fontFamily: "Poppins, sans-serif" }}
                                                >
                                                    <i style={{ fontStyle: "italic" }}>
                                                        {t("ImportData.NotMandatory")}
                                                    </i>
                                                </MenuItem>
                                            )}
                                            {fileTitles.map((item, index) => (
                                                <MenuItem
                                                    key={item + index}
                                                    value={item}
                                                    onClick={() => handleChangeValue(titleKey, item)}
                                                    sx={{ fontFamily: "Poppins, sans-serif" }}
                                                >
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </StyledErrorSelect>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            width: "320px",
                                            padding: "0 16px",
                                            borderRight: borderValue,
                                            fontFamily: "Poppins, sans-serif",
                                        }}
                                        id={`data-${index}`}
                                    >
                                        {loadedData.length !== 0 &&
                                        columnSuccessfullyMapped &&
                                        selectValues[titleValue] !== "NotMandatory"
                                            ? loadedData[0][titleValue]
                                            : "--"}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default MappingColumns;
